import CollapseButton from '@orn/shared-web/components/Button/CollapseButton';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

interface IWidgetContextValue {
  /** Whether the body is currently collapsed (hidden). Only meaningful when `isCollapsible` is true. */
  isCollapsed: boolean;
  /** Toggle `isCollapsed`. Wired to the chevron button rendered by `Widget.Header`. */
  onToggle: () => void;
  /**
   * Whether this widget can be collapsed/expanded by the user.
   * When true, `Widget.Header` renders a `CollapseButton` and applies tighter
   * vertical padding to accommodate it.
   */
  isCollapsible: boolean;
  /**
   * Flat mode — renders the widget as a borderless section inside a parent card
   * instead of as a standalone card. Strips background, shadow, and rounding from
   * the outer container and applies denser header padding so nested groups don't
   * look like cards-within-cards.
   */
  flat: boolean;
}

const WidgetContext = React.createContext<IWidgetContextValue | null>(null);

interface IWidgetProps extends React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /** Render as a flat section inside a parent card (no bg, shadow, or rounding). */
  flat?: boolean;
}

const Widget = ({
  children,
  className,
  collapsible = false,
  defaultCollapsed = false,
  flat = false,
  ...props
}: IWidgetProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed);

  const onToggle = React.useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const contextValue = React.useMemo<IWidgetContextValue>(
    () => ({
      isCollapsed,
      onToggle,
      isCollapsible: collapsible,
      flat,
    }),
    [isCollapsed, onToggle, collapsible, flat]
  );

  return (
    <WidgetContext.Provider value={collapsible || flat ? contextValue : null}>
      <div
        className={cx(
          'flex flex-col',
          !flat && 'bg-secondary_subtle border-secondary shadow-card overflow-hidden rounded-2xl border',
          className
        )}
        {...props}
      >
        {children}
      </div>
    </WidgetContext.Provider>
  );
};

interface IWidgetHeaderProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'title'> {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  trailing?: React.ReactNode;
}

const WidgetHeader = ({ title, subtitle, trailing, className, ...props }: IWidgetHeaderProps) => {
  const context = React.useContext(WidgetContext);
  const isCollapsible = context?.isCollapsible ?? false;
  const isEmbedded = context?.flat ?? false;

  return (
    <div
      className={cx(
        'border-tertiary flex items-center justify-between border-b',
        isEmbedded ? 'py-3 pl-5 pr-4' : cx('bg-secondary p-5', isCollapsible && 'py-4.25 px-5'),
        className
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-1 flex-col items-start justify-center">
        <div className="text-primary truncate text-lg font-semibold">{title}</div>
        {subtitle && <div className="text-tertiary truncate text-sm font-normal">{subtitle}</div>}
      </div>
      <div className={cx('flex shrink-0 items-center justify-center self-center', isEmbedded ? 'gap-0.5' : 'gap-2')}>
        {trailing}
        {isCollapsible && context && (
          <CollapseButton isCollapsed={context.isCollapsed} onToggle={() => context.onToggle()} />
        )}
      </div>
    </div>
  );
};

interface IWidgetBodyProps extends React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;
}

const WidgetBody = ({ children, className, ...props }: IWidgetBodyProps) => {
  const context = React.useContext(WidgetContext);
  const isCollapsed = context?.isCollapsed ?? false;

  if (isCollapsed) {
    return null;
  }

  return (
    <div className={cx('flex flex-col', className)} {...props}>
      {children}
    </div>
  );
};

interface IWidgetRowProps extends React.ComponentPropsWithoutRef<'div'> {
  label: React.ReactNode;
  value?: React.ReactNode;
  valueClassName?: string;
  icon?: React.ReactNode;
  subline?: React.ReactNode;
}

const WidgetRow = ({ label, value, valueClassName, icon, subline, className, ...props }: IWidgetRowProps) => {
  const labelId = React.useId();
  return (
    <div className={cx('flex items-center justify-between gap-5 px-5 py-3', className)} {...props}>
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
        {icon && <div className="shrink-0">{icon}</div>}
        <div id={labelId} className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {label}
          {subline && <div className="text-tertiary truncate text-sm font-normal">{subline}</div>}
        </div>
      </div>
      {value && (
        <span
          aria-labelledby={labelId}
          className={cx('text-md shrink-0 font-medium', valueClassName || 'text-primary')}
        >
          {value}
        </span>
      )}
    </div>
  );
};

Widget.Header = WidgetHeader;
Widget.Body = WidgetBody;
Widget.Row = WidgetRow;

export default Widget;
